import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, LogIn, LayoutDashboard, Calendar, MapPin } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import LoginDialog from "./LoginDialog";
import AssessmentRequestModal from "./AssessmentRequestModal";
import logoMark from "@/assets/logo-msc-mark.svg";
import { PROGRAMS } from "@/lib/programs";
import { useAuth } from "@/context/AuthContext";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [assessOpen, setAssessOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    const firstName = (user?.name || "").split(" ")[0];
    const initial = firstName ? firstName.charAt(0).toUpperCase() : "A";

    const scrollToVisit = (e) => {
        e.preventDefault();
        const el = document.getElementById("visit");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const isAppPage = location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/coach");
    const solidBg = scrolled || isAppPage;

    return (
        <>
            <header
                data-testid="site-navbar"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    solidBg
                        ? "bg-black/85 backdrop-blur-md border-b border-zinc-800"
                        : "bg-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                    <Link to="/" data-testid="nav-logo" className="flex items-center group">
                        <img
                            src={logoMark}
                            alt="Maximum Sports Conditioning"
                            className="h-10 md:h-12 w-auto transition-transform group-hover:scale-[1.04]"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-1">
                        <NavLink
                            to="/"
                            end
                            data-testid="nav-home-link"
                            className={({ isActive }) =>
                                `px-4 py-2 text-sm font-medium uppercase tracking-[0.15em] transition-colors ${
                                    isActive ? "text-brand-red" : "text-white hover:text-brand-red"
                                }`
                            }
                        >
                            Home
                        </NavLink>

                        <DropdownMenu>
                            <DropdownMenuTrigger
                                data-testid="nav-programs-trigger"
                                className="px-4 py-2 text-sm font-medium uppercase tracking-[0.15em] text-white hover:text-brand-red transition-colors inline-flex items-center gap-1.5 outline-none"
                            >
                                Programs
                                <ChevronDown className="w-3.5 h-3.5" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                sideOffset={12}
                                className="bg-zinc-950 border border-zinc-800 rounded-none p-0 w-72"
                            >
                                {PROGRAMS.map((p) => (
                                    <DropdownMenuItem
                                        key={p.slug}
                                        asChild
                                        className="rounded-none focus:bg-brand-red/10 focus:text-white p-0"
                                    >
                                        <Link
                                            to={p.path}
                                            data-testid={`nav-program-${p.slug}`}
                                            className="block px-5 py-4 border-b border-zinc-900 last:border-b-0 group cursor-pointer"
                                        >
                                            <div className="font-display text-2xl tracking-wider group-hover:text-brand-red transition-colors leading-tight">
                                                {p.title}
                                            </div>
                                            <div className="text-[11px] tracking-[0.2em] text-brand-navy-300 uppercase mt-1.5">
                                                {p.eyebrow}
                                            </div>
                                        </Link>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <Link
                            to="/coaches"
                            data-testid="nav-coaches-link"
                            className="px-4 py-2 text-sm font-medium uppercase tracking-[0.15em] text-white hover:text-brand-red transition-colors"
                        >
                            Coaches
                        </Link>

                        <Link
                            to="/careers"
                            data-testid="nav-careers-link"
                            className="px-4 py-2 text-sm font-medium uppercase tracking-[0.15em] text-white hover:text-brand-red transition-colors"
                        >
                            Careers
                        </Link>

                        <a
                            href="#visit"
                            onClick={scrollToVisit}
                            data-testid="nav-visit-link"
                            className="px-4 py-2 text-sm font-medium uppercase tracking-[0.15em] text-white hover:text-brand-red transition-colors inline-flex items-center gap-1.5"
                        >
                            <MapPin className="w-3.5 h-3.5" /> Visit
                        </a>

                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger
                                    data-testid="nav-user-trigger"
                                    className="ml-3 inline-flex items-center gap-3 pl-2 pr-3 py-1.5 border border-zinc-800 hover:border-brand-red transition-colors outline-none"
                                >
                                    <span className="inline-flex items-center justify-center w-7 h-7 bg-brand-red text-white font-display text-sm">
                                        {initial}
                                    </span>
                                    <span className="text-xs font-medium uppercase tracking-[0.15em] text-white">
                                        {firstName}
                                    </span>
                                    <ChevronDown className="w-3.5 h-3.5 text-zinc-300" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    sideOffset={12}
                                    className="bg-zinc-950 border border-zinc-800 rounded-none p-0 w-56"
                                >
                                    <div className="px-4 py-3 border-b border-zinc-900">
                                        <div className="text-sm text-white font-semibold truncate">{user.name}</div>
                                        <div className="text-xs text-zinc-300 truncate">{user.email}</div>
                                    </div>
                                    <DropdownMenuItem
                                        asChild
                                        className="rounded-none focus:bg-brand-red/10 focus:text-white p-0"
                                    >
                                        <Link
                                            to={user.role === "owner" || user.role === "coach" ? "/coach" : "/dashboard"}
                                            data-testid="nav-user-dashboard"
                                            className="px-4 py-3 inline-flex items-center gap-2 text-sm text-white"
                                        >
                                            <LayoutDashboard className="w-4 h-4 text-brand-red" />
                                            {user.role === "owner" || user.role === "coach" ? "Coach console" : "Dashboard"}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-zinc-900" />
                                    <DropdownMenuItem
                                        data-testid="nav-user-logout"
                                        onClick={handleLogout}
                                        className="rounded-none focus:bg-brand-red/10 focus:text-white px-4 py-3 inline-flex items-center gap-2 text-sm text-white cursor-pointer"
                                    >
                                        <LogOut className="w-4 h-4 text-brand-red" />
                                        Sign out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <button
                                type="button"
                                data-testid="nav-login-button"
                                onClick={() => setLoginOpen(true)}
                                className="ml-3 btn-ghost-tactical"
                            >
                                Sign in
                            </button>
                        )}
                        {!user && (
                            <button
                                type="button"
                                data-testid="nav-assessment-button"
                                onClick={() => setAssessOpen(true)}
                                className="ml-2 btn-tactical"
                            >
                                <Calendar className="w-4 h-4" /> Have questions? Book a consult!
                            </button>
                        )}
                    </nav>

                    {/* Mobile: visible sign-in + menu toggle */}
                    <div className="md:hidden flex items-center gap-2">
                        {!user && (
                            <button
                                type="button"
                                data-testid="nav-mobile-signin-visible"
                                onClick={() => setLoginOpen(true)}
                                className="inline-flex items-center gap-1.5 h-10 px-3 border border-brand-red/70 bg-brand-red/10 text-white text-[11px] font-medium uppercase tracking-[0.15em]"
                            >
                                <LogIn className="w-3.5 h-3.5 text-brand-red" /> Sign in
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="nav-mobile-toggle"
                            onClick={() => setMobileOpen((v) => !v)}
                            className="inline-flex items-center justify-center w-10 h-10 border border-zinc-800 text-white"
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileOpen && (
                    <div
                        className="md:hidden bg-black border-t border-zinc-800"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 space-y-1">
                            <Link
                                to="/"
                                data-testid="nav-mobile-home"
                                className="block px-2 py-3 text-sm font-medium uppercase tracking-[0.15em] text-white border-b border-zinc-900"
                            >
                                Home
                            </Link>
                            <div className="pt-3">
                                <div className="px-2 text-[10px] tracking-[0.25em] text-zinc-300 uppercase mb-2">
                                    Programs
                                </div>
                                {PROGRAMS.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={p.path}
                                        data-testid={`nav-mobile-program-${p.slug}`}
                                        className="block px-2 py-3 border-b border-zinc-900"
                                    >
                                        <div className="font-display text-2xl tracking-wider leading-tight">
                                            {p.title}
                                        </div>
                                        <div className="text-[11px] tracking-[0.2em] text-brand-navy-300 uppercase mt-1.5">
                                            {p.eyebrow}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                            <Link
                                to="/coaches"
                                onClick={() => setMobileOpen(false)}
                                data-testid="nav-mobile-coaches"
                                className="block px-2 py-3 mt-2 text-sm font-medium uppercase tracking-[0.15em] text-white border-b border-zinc-900"
                            >
                                Coaches
                            </Link>
                            <Link
                                to="/careers"
                                onClick={() => setMobileOpen(false)}
                                data-testid="nav-mobile-careers"
                                className="block px-2 py-3 mt-2 text-sm font-medium uppercase tracking-[0.15em] text-white border-b border-zinc-900"
                            >
                                Careers
                            </Link>
                            <a
                                href="#visit"
                                onClick={(e) => { setMobileOpen(false); scrollToVisit(e); }}
                                data-testid="nav-mobile-visit"
                                className="block px-2 py-3 mt-2 text-sm font-medium uppercase tracking-[0.15em] text-white border-b border-zinc-900 inline-flex items-center gap-2"
                            >
                                <MapPin className="w-4 h-4 text-brand-red" /> Hours &amp; Directions
                            </a>
                            {user ? (
                                <div className="pt-4 space-y-2">
                                    {/* Role-aware: coaches/owners go to the coach console,
                                        NOT /dashboard. This mirrors the desktop dropdown
                                        (lines 176-183) so mobile coaches never get bounced
                                        through the member dashboard (which briefly flashes
                                        "Member · MaxSC" before redirecting). Sachin
                                        2026-02-13: BrianL@maxsc.net coach experience. */}
                                    <Link
                                        to={user.role === "owner" || user.role === "coach" ? "/coach" : "/dashboard"}
                                        onClick={() => setMobileOpen(false)}
                                        data-testid="nav-mobile-dashboard"
                                        className="btn-ghost-tactical w-full justify-center"
                                    >
                                        <LayoutDashboard className="w-4 h-4" />
                                        {user.role === "owner" || user.role === "coach" ? "Coach console" : "Dashboard"}
                                    </Link>
                                    <button
                                        type="button"
                                        data-testid="nav-mobile-logout"
                                        onClick={handleLogout}
                                        className="btn-tactical w-full justify-center"
                                    >
                                        <LogOut className="w-4 h-4" /> Sign out ({firstName})
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        data-testid="nav-mobile-assessment-button"
                                        onClick={() => {
                                            setMobileOpen(false);
                                            setAssessOpen(true);
                                        }}
                                        className="btn-tactical w-full justify-center mt-4"
                                    >
                                        <Calendar className="w-4 h-4" /> Have questions? Book a consult!
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="nav-mobile-login-button"
                                        onClick={() => {
                                            setMobileOpen(false);
                                            setLoginOpen(true);
                                        }}
                                        className="btn-ghost-tactical w-full justify-center mt-2"
                                    >
                                        Sign in
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </header>

            <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
            <AssessmentRequestModal open={assessOpen} onClose={() => setAssessOpen(false)} />
        </>
    );
}
